import { Component, NgZone } from '@angular/core'
import { CommonModule } from '@angular/common'
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms'
import { ActivatedRoute, Router, RouterModule } from '@angular/router'
import { LoaderService } from '../../../services/loader.service'
import { AgentBookingService } from '../../../services/agent-booking.service'
import { UserAuthService } from '../../../services/user-auth.service'
import { CustomBxInputComponent } from '../../../components/custom-bx-input/custom-bx-input.component'
import { AgentHeaderComponent } from '../../../components/agent-header/agent-header.component'
import { EMAIL_REGEXP } from '../../../common/validators/sharedRegex'
import { invalidDateValidator, nameValidator } from '../../../common/validators/custom-validators'
import { environment } from '../../../../environments/environment'

@Component({
    imports: [CommonModule, FormsModule, ReactiveFormsModule, RouterModule, CustomBxInputComponent, AgentHeaderComponent],
    selector: 'app-agent-create-user',
    templateUrl: './agent-create-user.component.html',
    styleUrls: ['./agent-create-user.component.scss']
})
export class AgentCreateUserComponent {
  createUserForm!: FormGroup
  submitted = false
  consentChecked = false
  errorMessage = ''
  emailErrorMessages = {
    required: () => 'Please enter an email address',
    pattern: () => 'Please enter a valid email address'
  }

  constructor (
    private formBuilder: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private loaderService: LoaderService,
    private agentBookingService: AgentBookingService,
    private userAuthService: UserAuthService,
    private ngZone: NgZone
  ) {}

  ngOnInit (): void {
    const savedEmail = sessionStorage.getItem('agentTtEmail') || ''
    const savedDob = sessionStorage.getItem('agentTtDob') || ''
    let dobValue: any = ''
    if (savedDob) {
      // Convert from YYYY-MM-DD to NgbDateStruct { year, month, day } for date picker
      const parts = savedDob.split('-')
      if (parts.length === 3) {
        dobValue = { year: parseInt(parts[0], 10), month: parseInt(parts[1], 10), day: parseInt(parts[2], 10) }
      } else {
        dobValue = savedDob
      }
    }

    this.createUserForm = this.formBuilder.group({
      firstName: ['', [Validators.required, Validators.maxLength(50)]],
      lastName: ['', [Validators.required, Validators.maxLength(50)]],
      email: [{ value: savedEmail, disabled: true }, [Validators.required, Validators.pattern(EMAIL_REGEXP)]],
      dob: [{ value: dobValue, disabled: true }, [Validators.required, invalidDateValidator()]]
    },
    { validators: [nameValidator] })
  }

  get f () {
    return this.createUserForm.controls
  }

  onConfirmAndProceed (): void {
    this.submitted = true
    this.errorMessage = ''
    if (this.createUserForm.invalid || !this.consentChecked) {
      return
    }

    this.loaderService.show()
    const formValue = this.createUserForm.getRawValue()

    this.agentBookingService.createUserProfile(
      formValue.firstName,
      formValue.lastName,
      formValue.email,
      this.formatDob(formValue.dob)
    ).subscribe({
      next: (res) => {
        this.loaderService.hide()
        sessionStorage.setItem('agentTtSub', res.sub)
        sessionStorage.setItem('agentTtUserProfileId', res.id || res.userProfileId)
        sessionStorage.setItem('agentTtFirstName', formValue.firstName)
        sessionStorage.setItem('agentTtLastName', formValue.lastName)
        this.userAuthService.setCookie('agentTtProfile', JSON.stringify(res))
        const queryParams = this.route.snapshot.queryParams
        const params = new URLSearchParams({ ...queryParams, agentFlow: 'true' }).toString()
        window.location.href = `${environment.bxURL}/account/agent-consent-upload?${params}`
      },
      error: (err) => {
        this.loaderService.hide()
        this.errorMessage = err?.error?.message || 'Something went wrong. Please try again.'
      }
    })
  }

  private formatDob (dob: any): string {
    if (typeof dob === 'string') {
      if (/^\d{4}-\d{2}-\d{2}$/.test(dob)) {
        return dob
      }
      const parts = dob.split('/')
      if (parts.length === 3) {
        const [day, month, year] = parts
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
      }
      return ''
    }
    if (dob && dob.year && dob.month && dob.day) {
      const month = String(dob.month).padStart(2, '0')
      const day = String(dob.day).padStart(2, '0')
      return `${dob.year}-${month}-${day}`
    }
    return ''
  }

  toggleConsent (): void {
    this.consentChecked = !this.consentChecked
  }

  goBack (): void {
    window.history.back()
  }
}
